import { usernameProblem } from "@lorevok/domain";
import { useCallback, useEffect, useRef, useState } from "react";
import { checkUsername } from "../apiClient.js";

/** Where a username typed into the sign-up form stands. */
export type UsernameStatus =
  | "empty"
  | "invalid"
  | "reserved"
  | "rejected"
  | "checking"
  | "available"
  | "taken"
  | "error";

const CHECK_DELAY_MS = 400;

type Checker = (username: string) => Promise<{ available: boolean; reason: string | null }>;

function localStatus(username: string): UsernameStatus | null {
  return username ? usernameProblem(username) : "empty";
}

function statusFromAnswer(answer: { available: boolean; reason: string | null }): UsernameStatus {
  if (answer.available) return "available";
  return answer.reason === "invalid" || answer.reason === "reserved" || answer.reason === "rejected"
    ? answer.reason
    : "taken";
}

/**
 * Format + reserved checks as the visitor types, and an availability check
 * against the API once they pause. `confirm()` re-checks right away (for
 * submit) and resolves to the settled status, so the form never submits a name
 * that was taken a moment ago or is still being checked.
 */
export function useUsernameAvailability(value: string, check: Checker = checkUsername) {
  const username = value.trim();
  const [status, setStatus] = useState<UsernameStatus>(() => localStatus(username) ?? "checking");
  const latest = useRef(username);
  latest.current = username;
  const checkRef = useRef(check);
  checkRef.current = check;

  const runCheck = useCallback(async (name: string): Promise<UsernameStatus> => {
    let next: UsernameStatus;
    try {
      next = statusFromAnswer(await checkRef.current(name));
    } catch {
      next = "error";
    }
    if (latest.current === name) setStatus(next);
    return next;
  }, []);

  useEffect(() => {
    const local = localStatus(username);
    if (local) {
      setStatus(local);
      return;
    }
    setStatus("checking");
    const timer = setTimeout(() => void runCheck(username), CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [username, runCheck]);

  const confirm = useCallback(async (): Promise<UsernameStatus> => {
    const name = latest.current;
    const local = localStatus(name);
    if (local) return local;
    setStatus("checking");
    return runCheck(name);
  }, [runCheck]);

  return { username, status, confirm };
}

/** The message for a status that blocks submitting, or null. */
export function usernameStatusMessageKey(
  status: UsernameStatus
):
  | "username.invalid"
  | "username.reserved"
  | "username.rejected"
  | "username.taken"
  | "username.checkFailed"
  | null {
  switch (status) {
    case "empty":
    case "invalid":
      return "username.invalid";
    case "reserved":
      return "username.reserved";
    case "rejected":
      return "username.rejected";
    case "taken":
      return "username.taken";
    case "error":
      return "username.checkFailed";
    default:
      return null;
  }
}
